import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { z } from 'zod/v4';
import { type ApiError, type ApiResponse, useAxiosInstance } from './utils';
import { addActionNameToMutationResult } from './utils/add-action-name-to-mutation-result';

const deleteCommentVariablesSchema = z.object({
  id: z.number().or(z.string().transform(Number)),
});

export type DeleteCommentVariables = z.infer<typeof deleteCommentVariablesSchema>;

export async function deleteCommentFn(
  axiosInstance: Promise<AxiosInstance>,
  data: DeleteCommentVariables,
) {
  const instance = await axiosInstance;
  return instance.delete(`/comments/${data.id}`);
}

export function useDeleteComment() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, DeleteCommentVariables>({
    mutationFn: variables => deleteCommentFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['comments'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    DeleteCommentVariables,
    'deleteComment'
  >('deleteComment', mutation);
}
